'use client';

import clsx from 'clsx';
import ReactFocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';

import { Anchor, AnchorProps } from '../anchor';
import { Button, type ButtonProps } from '../button';
import { useModal } from './modal.context';
import styles from './modal.module.scss';

const ModalOpenButton = ({ children, ...props }: ButtonProps) => {
  const { open } = useModal();

  return (
    <Button type='button' {...props} onClick={open} aria-label='Close Modal'>
      {children}
    </Button>
  );
};

const ModalOpenLink = ({ children, ...props }: Omit<AnchorProps, 'href'>) => {
  const { open } = useModal();
  return (
    <Anchor href='#' {...props} onClick={open}>
      {children}
    </Anchor>
  );
};

const Modal = ({
  children,
  limitWindowHeight,
}: { limitWindowHeight?: boolean } & React.PropsWithChildren) => {
  const { isOpen, close } = useModal();

  return isOpen ? (
    <ReactFocusLock returnFocus>
      <RemoveScroll>
        <div
          data-testid='modal-wrapper'
          className={clsx({
            [styles['modal__wrapper']]: true,
            [styles['modal__wrapper--scrollable']]: limitWindowHeight,
          })}
        >
          <button
            data-testid='modal-backdrop'
            className={styles['backdrop']}
            onClick={close}
            type='button'
          />
          <div
            data-testid='modal'
            className={clsx({
              [styles['modal']]: true,
              [styles['modal--no-max-height']]: limitWindowHeight,
            })}
          >
            {children}
          </div>
        </div>
      </RemoveScroll>
    </ReactFocusLock>
  ) : null;
};

const ModalHeader = ({ children }: React.PropsWithChildren) => (
  <div className={styles['modal__head']}>{children}</div>
);

const ModalTitle = ({ children }: React.PropsWithChildren) => (
  <div className={styles['modal__title']}>{children}</div>
);

const ModalCloseButton = ({
  float = false,
  inset,
  dark,
  children,
}: {
  float?: boolean;
  inset?: boolean;
  dark?: boolean;
} & React.PropsWithChildren) => {
  const { close } = useModal();
  return (
    <button
      className={clsx({
        [styles['modal__close']]: true,
        [styles['modal__close--float']]: float,
        [styles['modal__close--inset']]: inset,
        [styles['modal__close--dark']]: dark,
      })}
      type='button'
      onClick={close}
      aria-label='Close modal'
    >
      {children}
    </button>
  );
};

const ModalDismissButton = ({ children, ...props }: ButtonProps) => {
  const { close } = useModal();
  return (
    <Button {...props} onClick={close}>
      {children}
    </Button>
  );
};

const ModalBody = ({
  padding = true,
  scrollable = true,
  dark,
  children,
}: {
  padding?: boolean;
  dark?: boolean;
  scrollable?: boolean;
} & React.PropsWithChildren) => (
  <div
    className={clsx({
      [styles['modal__body']]: true,
      [styles['modal__body--dark']]: dark,
      [styles['modal__body--scrollable']]: scrollable,
      [styles['modal__body--no-padding']]: !padding,
    })}
  >
    {children}
  </div>
);

const ModalFooter = ({ children }: React.PropsWithChildren) => (
  <div className={styles['modal__foot']}>{children}</div>
);

export {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalDismissButton,
  ModalFooter,
  ModalHeader,
  ModalOpenButton,
  ModalOpenLink,
  ModalTitle,
};
